import type { DetrixNetworkEntry, DetrixSession } from '../types';
import { formatDuration, formatSessionOffset } from './time';
import { prettifyJsonString } from './prettifyJson';

const REDACT_KEYS = new Set(
  [
    'authorization',
    'cookie',
    'proxy-authorization',
    'set-cookie',
    'x-api-key',
    'x-auth-token',
    'x-amz-security-token',
    'x-goog-api-key',
  ].map((k) => k.toLowerCase()),
);

function shouldRedactHeader(name: string): boolean {
  const n = name.toLowerCase();
  return REDACT_KEYS.has(n);
}

function redactHeaderValue(name: string, value: string): string {
  return shouldRedactHeader(name) ? '[redacted]' : value;
}

function sortedHeaderLines(headers: Record<string, string>): string {
  if (!headers || Object.keys(headers).length === 0) return '_(none)_';
  return Object.entries(headers)
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([k, v]) => `${k}: ${redactHeaderValue(k, v)}`)
    .join('\n');
}

function truncateText(s: string | null | undefined, maxChars: number): string {
  if (s == null || s === '') return '_(empty)_';
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars)}\n\n… _(${s.length - maxChars} more characters truncated)_`;
}

function buildCurlRedacted(entry: DetrixNetworkEntry): string {
  const { request } = entry;
  let curl = `curl -X '${request.method}' \\\n  '${request.url}'`;

  const headers = Object.entries(request.headers).sort(([a], [b]) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  for (const [key, value] of headers) {
    const v = redactHeaderValue(key, value).replace(/'/g, `'\\''`);
    curl += ` \\\n  -H '${key}: ${v}'`;
  }

  if (request.body && request.body.length > 0 && request.body.length < 10_000) {
    const escapedBody = request.body.replace(/'/g, `'\\''`);
    curl += ` \\\n  --data '${escapedBody}'`;
  }

  return curl;
}

/** Markdown bug report for the selected page / fetch / XHR network row (clipboard). */
export function buildNetworkBugReportMarkdown(session: DetrixSession, entry: DetrixNetworkEntry): string {
  const status = entry.response?.status ?? 0;
  const statusLine = entry.response
    ? `${status}${entry.response.statusText ? ` ${entry.response.statusText}` : ''}`.trim()
    : entry.error?.message ?? 'No response';

  const resBody = entry.response?.body ?? null;
  const capturedIso = new Date().toISOString();

  const reqHeaders = sortedHeaderLines(entry.request.headers);
  const resHeaders = entry.response ? sortedHeaderLines(entry.response.headers) : '_(no response)_';

  const parts: string[] = [
    `## Network capture (Detrix Session Viewer)`,
    ``,
    `- **Captured:** ${capturedIso}`,
    `- **Session:** \`${session.sessionId}\` · offset **${formatSessionOffset(entry.timestamp)}**`,
    `- **Method:** ${entry.request.method}`,
    `- **URL:** ${entry.request.url}`,
    `- **Status:** ${statusLine}`,
    `- **Duration:** ${formatDuration(entry.timing.durationMs)}`,
    `- **Type:** page (fetch/XHR) · request encoding: ${entry.request.bodyEncoding}${entry.response ? ` · response encoding: ${entry.response.bodyEncoding}` : ''}`,
    ``,
    `### Request headers (redacted)`,
    `\`\`\``,
    reqHeaders,
    `\`\`\``,
    ``,
    `### Response headers (redacted)`,
    `\`\`\``,
    resHeaders,
    `\`\`\``,
    ``,
  ];

  if (entry.request.body && entry.request.body.length > 0) {
    parts.push(
      `### Request body (truncated)`,
      `\`\`\`json`,
      truncateText(prettifyJsonString(entry.request.body), 6000),
      `\`\`\``,
      ``,
    );
  }

  parts.push(
    `### Response body (redacted, truncated)`,
    `\`\`\`json`,
    truncateText(prettifyJsonString(resBody), 6000),
    `\`\`\``,
    ``,
    `### cURL (redacted)`,
    `\`\`\`bash`,
    buildCurlRedacted(entry),
    `\`\`\``,
  );

  return parts.join('\n');
}
